import { useState } from 'react';
import { Popover } from 'antd';
import { Checkbox } from 'antd';
import { Badge } from 'antd';
import { Button, ButtonProps } from '@insight-health-ai/roseate/components';
import { LoadingOutlined } from '@ant-design/icons';
import { Hourglass01 } from '@insight-health-ai/roseate/icons';
import { ServiceRequestStatus as ServiceRequestStatusType } from 'src/models';
import { useGetServiceRequestStatuses } from 'src/hooks/serviceRequest/useGetServiceRequestStatuses';
import { ServiceRequestStatus } from './serviceRequestStatus';

export type StatusDropdownProps = {
  value: ServiceRequestStatusType['status_id'][];
  onChange: (value: ServiceRequestStatusType['status_id'][]) => void;
  buttonProps?: ButtonProps;
};

export const ServiceRequestStatusFilter = ({
  value,
  onChange,
  buttonProps,
}: StatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedValues = Array.isArray(value) ? value : [];

  const onStatusChange = (
    checked: boolean,
    statusId: ServiceRequestStatusType['status_id']
  ) => {
    if (checked) {
      onChange(Array.from(new Set([...selectedValues, statusId])));
    } else {
      onChange(selectedValues.filter((t) => t !== statusId));
    }
  };

  const {
    data: statuses,
    isLoading,
    isFetching,
  } = useGetServiceRequestStatuses();

  return (
    <Popover
      classNames={{
        body: 'min-w-[200px] max-w-[320px] max-h-[320px] overflow-auto',
      }}
      content={
        <div className="flex flex-col">
          <div className="font-medium text-sm mb-4 flex items-center gap-2">
            <Hourglass01 /> Filter by status
            {selectedValues.length > 0 && (
              <a
                className="text-xs text-gray-600 font-semibold right-0 ml-auto cursor-pointer"
                onClick={() => {
                  onChange([]);
                }}
              >
                Clear
              </a>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {statuses?.map((status) => {
              const isSelected = selectedValues.some(
                (t) => t === status.status_id
              );
              return (
                <div key={status.status_id} className="flex items-center gap-2">
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      onStatusChange(e.target.checked, status.status_id);
                    }}
                  >
                    <ServiceRequestStatus
                      status={status}
                      statusProps={{
                        size: 'xs',
                      }}
                    />
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
      }
      trigger="click"
      placement="bottomRight"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Badge count={selectedValues.length} offset={[0, 7]}>
        <Button
          variant="outlined"
          appearance="secondary"
          disabled={isLoading}
          icon={isLoading || isFetching ? <LoadingOutlined /> : <Hourglass01 />}
          label="Status"
          {...buttonProps}
        />
      </Badge>
    </Popover>
  );
};
